import React, { useCallback } from "react";
import {
  ABC_ROW_HEADERS,
  XYZ_BEHAVIOR_DESCRIPTIONS,
  XYZ_COLUMN_LABELS,
} from "../constants";
import { getCellPresentation } from "../utils/cellIntensity";
import ABCXYZInfoTooltip from "./ABCXYZInfoTooltip";

const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString("de-DE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const CrossMatrixCell = ({
  cell,
  intensity = 0,
  onCellClick = null,
  objectType = null,
}) => {
  const empty = cell.combination_count === 0;
  const cellNumberLines = [];
  if (objectType) {
    cellNumberLines.push(
      {
        label: `${objectType} ABC: ${cell.object_class}`,
        text: `${ABC_ROW_HEADERS[cell.object_class]?.object || ""} — this cell's object-group importance class.`,
      },
      {
        label: `Variant ABC: ${cell.variant_class}`,
        text: `${ABC_ROW_HEADERS[cell.variant_class]?.variant || ""} — this cell's process-variant importance class.`,
      },
    );

    if (!empty && cell.dominant_xyz && XYZ_COLUMN_LABELS[cell.dominant_xyz]) {
      cellNumberLines.push({
        label: `XYZ: ${cell.dominant_xyz} — ${XYZ_COLUMN_LABELS[cell.dominant_xyz]}`,
        text:
          `Combined stability of "${objectType}" × variant combinations in ` +
          `this cell: it ${XYZ_BEHAVIOR_DESCRIPTIONS[cell.dominant_xyz]}. ` +
          `One vote per combination, never per underlying object.`,
      });
    }

    cellNumberLines.push(
      {
        label: formatNumber(cell.combination_count),
        text: `unique "${objectType}" × variant combinations in this cell.`,
      },
      {
        label: formatNumber(cell.object_count),
        text: `underlying "${objectType}" objects across those combinations.`,
      },
    );
  }

  const handleClick = useCallback(() => {
    if (onCellClick) onCellClick(cell);
  }, [cell, onCellClick]);

  const presentation = empty
    ? { style: {}, darkText: true }
    : getCellPresentation(cell.dominant_xyz, intensity);

  // Light-text variants are the fallback for higher intensity bounds.
  const tones = presentation.darkText
    ? {
        count: "text-gray-900",
        percentage: empty ? "text-gray-400" : "text-gray-600",
        label: empty ? "text-gray-400" : "text-gray-600",
        cases: empty ? "text-gray-400" : "text-gray-600",
      }
    : {
        count: "text-white",
        percentage: "text-white/90",
        label: "text-white/90",
        cases: "text-white/75",
      };

  const cellBody = (
    <div className="h-full flex flex-col items-center justify-center text-center gap-1.5 leading-snug cursor-default">
      <div className="flex items-baseline justify-center gap-1.5">
        <span className={`text-xl font-semibold ${tones.count}`}>
          {formatNumber(cell.combination_count)}
        </span>
        <span className={`text-sm font-medium ${tones.percentage}`}>
          ({formatNumber(cell.percentage, 1)} %)
        </span>
      </div>
      <div className={`text-sm font-medium ${tones.label}`}>{cell.label}</div>
      <div className={`text-xs ${tones.cases}`}>{cell.decision}</div>
      <div className={`text-xs ${tones.cases}`}>
        {formatNumber(cell.object_count)} objects
      </div>
    </div>
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      style={presentation.style}
      className={`w-full h-full min-h-[114px] rounded-lg border p-2.5 transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
        empty ? "bg-gray-50 border-gray-200 opacity-60" : ""
      }`}
    >
      {cellNumberLines.length > 0 ? (
        <ABCXYZInfoTooltip
          title="Matrix Cell"
          sections={[]}
          extraLines={cellNumberLines}
          trigger={cellBody}
        />
      ) : (
        cellBody
      )}
    </button>
  );
};

export default CrossMatrixCell;
